import { OptionsListOption } from "@app/shared/qes-standards/forms/OptionsList";
import { FC, Fragment, useMemo } from "react";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import { ResourceTagModel } from "@app/tags/import/types";
import { ManageResourceTagDisplayModel } from "@app/tags/types";

interface TagResourceProps {
  resourceTags: ManageResourceTagDisplayModel[]
  tags?: ResourceTagModel[];
  disabled?: boolean;
  handleTaggingChange: (updatedTags: ResourceTagModel[]) => void;
}

const tagGroupNameError = "Group Name cannot be empty if a tag name is selected or created.";
const tagNameError = "Tag Name cannot be empty if a group name is selected or created.";

const isEmpty = (value?: string) => !value || value.trim().length === 0;

const taggingRequired = (tag: ResourceTagModel) => !isEmpty(tag.tagGroupName) || !isEmpty(tag.tagName);
const tagGroupNameOk = (tag: ResourceTagModel) => isEmpty(tag.tagName) || !isEmpty(tag.tagGroupName);
const tagNameOk = (tag: ResourceTagModel) => isEmpty(tag.tagGroupName) || !isEmpty(tag.tagName);

const TagResource: FC<React.PropsWithChildren<TagResourceProps>> = (props: TagResourceProps) => {
  const {
    disabled,
    tags,
    resourceTags
  } = props;

  const addTag = () => {
    const newTagResource: ResourceTagModel[] = [...tags, { tagGroupName: "", tagName: "" } as ResourceTagModel];
    props.handleTaggingChange(newTagResource);
  };

  const removeTag = (indexToRemove: number) => {
    const newTagResource: ResourceTagModel[] = tags.filter((_, index) => index !== indexToRemove);
    props.handleTaggingChange(newTagResource);
  };

  const onTagNameChange = (indexChanged: number, newTagNameValue: string) => {
    const updatedTagResources = tags.map((tag, index) =>
      index === indexChanged ? { ...tag, tagName: newTagNameValue } : tag
    );

    props.handleTaggingChange(updatedTagResources);
  };

  const onGroupNameChange = (indexChanged: number, newGroupNameValue: string) => {
    const updatedTagResources = tags.map((tag, index) =>
      index === indexChanged ? { ...tag, tagGroupName: newGroupNameValue } : tag
    );

    props.handleTaggingChange(updatedTagResources);
  };

  const tagGroupResources = useMemo(() => {
    const tagGroupNames = [...new Set(resourceTags.map(r => r.tagGroupName))];
    return tagGroupNames.map(groupItem => ({ key: groupItem, value: groupItem } as OptionsListOption));
  }, [resourceTags])

  const tagGroupNameToTagNamesDictionary = useMemo(() => {
    const dictionary: Record<string, OptionsListOption[]> = {};
    for (const r of resourceTags) {
      if (!dictionary[r.tagGroupName]) {
        dictionary[r.tagGroupName] = [];
      }
      if (r.tagName && !dictionary[r.tagGroupName].some(opt => opt.key === r.tagName)) {
        dictionary[r.tagGroupName].push({ key: r.tagName, value: r.tagName } as OptionsListOption);
      }
    }
    // Add a list of all tagNames to an empty key to allow retrieval of all names if no group is selected
    dictionary[""] = [...new Set(resourceTags.map(r => r.tagName))]
      .map(tagItem => ({ key: tagItem, value: tagItem } as OptionsListOption));
    return dictionary;
  }, [resourceTags]);

  return (
    <>
      {tags.map((tag, index) => (
        <Fragment key={`tag-resource-group-${index}`}>
          <div className="col-3 offset-2">
            <QESTextInput
              label="Group Name"
              placeholder="Select or create a group"
              maxLength={255}
              value={tag.tagGroupName}
              onChange={(e) => onGroupNameChange(index, e.target.value)}
              testId={`tag-resource__group-name-input--row-${index}`}
              descriptiveText={!tagGroupNameOk(tag) && tagGroupNameError}
              required={taggingRequired(tag)}
              isValid={tagGroupNameOk(tag)}
              wasFocusedExternally={true}
              disabled={disabled}
              optionsListConfig={{
                noMatchText: "No groups found.",
                options: tagGroupResources,
              }}
            />
          </div>
          <div className="col-3">
            <QESTextInput
              label="Tag Name"
              placeholder="Select or create a tag"
              maxLength={255}
              value={tag.tagName}
              onChange={(e) => onTagNameChange(index, e.target.value)}
              testId={`tag-resource__tag-name-input--row-${index}`}
              descriptiveText={!tagNameOk(tag) && tagNameError}
              required={taggingRequired(tag)}
              isValid={tagNameOk(tag)}
              wasFocusedExternally={true}
              disabled={disabled}
              optionsListConfig={{
                noMatchText: "No tags found.",
                options: tagGroupNameToTagNamesDictionary[tag.tagGroupName] ?? tagGroupNameToTagNamesDictionary[""],
              }}
            />
          </div>

          {tags.length > 1 && (
            <div className="col-2 align-content-center">
              <QESButton
                qesTestId={`tag-resource__remove-tag-button--row-${index}`}
                qesType="secondary-danger"
                onClick={() => removeTag(index)}
                disabled={disabled}>
                Remove Tag
              </QESButton>
            </div>
          )}
        </Fragment>
      ))}

      <div className="row col-10 offset-2">
        <QESButton
          className="col-auto"
          qesTestId="tag-resource__add-additional-tag-button"
          qesType="secondary"
          onClick={addTag}
          disabled={disabled}
        >
          <QesIcon iconName={QesIcons.Plus} isDecorative className="align-text-top" /> Add Additional Tag
        </QESButton>
      </div>
    </>
  );
};

export default TagResource;
